// BRK-S152 — the indemnitor ROSTER, as pure rules.
//
// Design: PMS-S341 (`docs/design/bond-indemnity-requirements.md`). On every bond
// submission Signal asks for personal indemnity from every owner holding AT
// LEAST the minimum share, topped up largest-share-first until the listed owners
// hold MORE THAN the represented share, plus the spouses of every listed owner.
//
// ── WHAT THIS MODULE IS NOT ─────────────────────────────────────────────────
//
// It is NOT the rule. Nothing here decides who signs: the evaluation is
// deterministic and belongs to discovery, PMS proxies it, and the order carries
// the answer (`BondIndemnity`). A client that re-derived the signer list would
// be a second answer to a question that has exactly one, and it would disagree
// the first time Product tuned a number.
//
// What this module owns is the ROSTER — the cap table the broker types in, and
// how its people map onto the contract's `personal[]` block. Adding an owner
// here edits the cap table; the signer list is then re-evaluated upstream
// (reconciliation decision (b)).
//
// ── A PERSON IS AN IDENTITY, NEVER A POSITION ───────────────────────────────
//
// A configuration declares `personal[].firstName`, and contract 3.0.0 /
// SUR-S065 says what that path's value is: a LIST of `{ownerId, value}`
// entries, keyed by the order's own OWNER ROSTER (`owners[]`), where a person
// who did not answer a field simply HAS NO ENTRY. The roster is declared by the
// caller on save, so an identity exists before the first keystroke.
//
// That is the whole addressing scheme here, and the reason an INDEX is not
// acceptable is the contract's own: the positional echo dropped holes, and a
// home address attributed to the wrong person is the worst kind of quiet defect
// — nothing errors, and the value is plausible where it lands. An earlier cut
// of this story keyed the bag `personal[0].firstName`; it is gone, along with
// every helper that spelled an index.
//
// ⚠️ ONE THING IS AHEAD OF THE BROKER WIRE. PMS's `broker-bond-view.ts`
// currently COLLAPSES entry lists at its boundary — "BRK has no multi-owner
// UI", it says, resolving each repeated path to the primary owner's scalar and
// publishing `elementCounts` beside it. This story is what makes that reason
// obsolete, so BRK reads entries (`elementEntries`) and writes them, and
// `rosterRowsFromValues` still understands a collapsed scalar as the PRIMARY
// owner's answer — which is every order filed before PMS-S393. See the report's
// question for PMS.
//
// ── SCOPED FIELD NAMES ARE UI STATE, AND NEVER REACH THE WIRE ───────────────
//
// The form's machinery — `unansweredFields`, `invalidFields`, the per-field
// error map, `BondFieldRenderer` — is keyed by ONE field name. A roster draws
// the same field for several people, so the name is SCOPED while it is in the
// form: `<ownerId>::personal[].firstName`. `rosterWireValues` turns those back
// into one entry list per contract path, so the scope exists only between the
// control and the save. `scopedRosterName` / `unscopeRosterName` are the only
// two places that spell it, and a test asserts no scoped key ever reaches a
// request body.
//
// ⚠️ ONE SCOPE, NEVER TWO (BRK-S154). `unscopeRosterName` peels exactly one, so
// a name scoped twice unscopes to a "contract path" that is still scoped — and
// that is what reached discovery, as a block nobody declared. `rosterFormValues`
// therefore works in CONTRACT PATHS whichever field list it is handed, and
// `invalidWireKeys` is the assertion at the one place a body is assembled.

import type { BondRenderField, BondValueBag } from "@/lib/bondFields";
import { fieldKind, isRepeatedPath } from "@/lib/bondFields";
import {
  elementEntries,
  entryForOwner,
  isIndemnityComplete,
  isIndemnitySupported,
  type BondElementValue,
  type BondFieldOption,
  type BondIndemnity,
  type BondIndemnityRule,
  type BondOwner,
  type BondOwnerDeclaration,
  type BondSigner,
} from "@/lib/brokerBonds";

// ── The contract paths this feature turns on ─────────────────────────────────
//
// Keyed by PATH, deliberately, and this is NOT the name-derived control
// inference D-10 rule 1 forbids. The control every one of these gets still comes
// from the configuration's declared `type` / `format` / `options` — nothing here
// looks at a name to decide what to draw. What the path decides is the value's
// ROLE in the roster: which answer says whether a spouse signs, which one is a
// share, which one links a spouse to an owner. That is the arrangement
// `COMPUTED` and `OUR_BID_KEYS` already use on the final step — a contract path
// read as a contract path.

/** Whether this person's spouse signs. A closed enum, see `MARITAL_STATUSES`. */
export const MARITAL_STATUS_PATH = "personal[].maritalStatus";
/** WHOSE spouse this person is — the owner's `ownerId` STRING, or `null` to
 *  clear the link. Structural: it is written, never drawn. */
export const SPOUSE_OF_PATH = "personal[].spouseOf";
/** The owner's share. A spouse has none. */
export const OWNERSHIP_PATH = "personal[].ownershipPercentage";

const FIRST_NAME_PATH = "personal[].firstName";
const LAST_NAME_PATH = "personal[].lastName";

/**
 * The paths an OWNER answers and a spouse does not.
 *
 * An exclusion list rather than an allow-list, and the direction matters: a
 * contract path added upstream then appears on BOTH blocks, which is a question
 * asked twice. An allow-list would silently stop asking a spouse for something
 * new, which is a question never asked at all — and the broker would have no
 * way to tell.
 */
const OWNER_ONLY_PATHS: readonly string[] = [
  OWNERSHIP_PATH,
  MARITAL_STATUS_PATH,
  SPOUSE_OF_PATH,
  "personal[].driversLicenseNumber",
  "personal[].driversLicenseState",
];

/**
 * The owner's date of birth, in BOTH spellings in play (BRK-S165).
 *
 * PMS's discovery stub declares `personal[].dateOfBirth` and this portal's dev
 * configuration declares `personal[].birthDate`. Which one a live product
 * carries is discovery's to say, and a spouse exemption keyed on ONE of them
 * would quietly require the other — the exact failure this resolver exists to
 * prevent, and an invisible one, since the field looks the same either way.
 * Reconciling the two spellings is a follow-up for the configuration, not a
 * reason for the rule to know only half of them.
 */
const DATE_OF_BIRTH_PATHS: readonly string[] = [
  "personal[].dateOfBirth",
  "personal[].birthDate",
];

/**
 * The paths a SPOUSE is never asked to complete, however the configuration
 * marks them (BRK-S165).
 *
 * SUR-S167 marks the owner's date of birth, SSN, ownership share and marital
 * status `required`, which is right for an owner and wrong for a spouse: the
 * two are drawn from the SAME `personal[]` block, so one `required` flag
 * arrives for both and a spouse would be held to answers nobody asks them for.
 *
 * An EXCLUSION list, for the reason `OWNER_ONLY_PATHS` is one: a path that
 * becomes required upstream then becomes required on both rows, which is a
 * question asked of somebody it should not be — visible, and an annoyance. An
 * allow-list would silently make it optional for a spouse, which is a question
 * that stops being asked with nothing on screen to say so.
 *
 * It is a superset of `OWNER_ONLY_PATHS`, whose members a spouse block does not
 * DRAW at all — one list would then be the rule and the other the exception, so
 * this one states both and the two cannot disagree about a spouse.
 */
const SPOUSE_NOT_ASKED_PATHS: readonly string[] = [
  ...OWNER_ONLY_PATHS,
  ...DATE_OF_BIRTH_PATHS,
];

/** Which row a roster field is drawn on. `RosterBlock["kind"]`, named so the
 *  resolver reads as a rule about a PERSON rather than about a block. */
export type RosterRole = "owner" | "spouse";

/**
 * Whether this person owes this field an answer — THE ONE requirement rule on
 * the roster (BRK-S165).
 *
 * The configuration decides whether a field is asked for at all; this decides
 * whether the person in front of it has to answer. `rosterBlocks` applies it as
 * it draws, so the required marker the broker sees, the per-card Save, the
 * Save & Continue gate and the resume-position rule all read ONE `required` —
 * which is the whole point. A renderer and a validator that resolved this
 * separately would eventually disagree, and the shape of that disagreement is a
 * card that shows no marker and then refuses to save.
 *
 * A spouse's HOME ADDRESS is required when it is asked for, and it is asked for
 * only when the broker ticks "spouse's address differs from the owner's" —
 * untucked, `rosterBlocks` omits the field entirely and the owner's address is
 * copied at save (`rosterWireValues`). So there is nothing to special-case
 * here: an absent field owes nothing.
 */
export function requiredForRole(
  field: BondRenderField,
  role: RosterRole,
): boolean {
  if (field.required !== true) return false;
  if (role === "owner") return true;
  return !SPOUSE_NOT_ASKED_PATHS.includes(contractPathOf(field.name));
}

// ── Marital status ───────────────────────────────────────────────────────────

/**
 * The marital-status vocabulary, and THE ONE CONSTANT that holds it.
 *
 * Three facts per member, in one place, because they are one decision: the wire
 * value, how it reads on screen, and whether it puts a spouse on the signer
 * list. Splitting the label map from the spouse rule is how a value comes to be
 * rendered by one and forgotten by the other.
 *
 * `spouseSigns` is BRK's mirror of the rule, used to decide whether to DRAW a
 * spouse block while the broker is still typing — the signer list itself is
 * upstream's, and the two agree because the vocabulary is closed.
 */
export const MARITAL_STATUSES = [
  { value: "single", label: "Single", spouseSigns: false },
  { value: "married", label: "Married", spouseSigns: true },
  {
    value: "domestic_partnership",
    label: "Domestic partnership",
    spouseSigns: true,
  },
  { value: "separated", label: "Separated", spouseSigns: false },
  { value: "divorced", label: "Divorced", spouseSigns: false },
  { value: "widowed", label: "Widowed", spouseSigns: false },
] as const;

export type BondMaritalStatus = (typeof MARITAL_STATUSES)[number]["value"];

/**
 * The options for the marital-status select, when the CONFIGURATION declares
 * none.
 *
 * A degraded fallback and nothing more (D-10 rule 1): the configuration owns its
 * own wording, so declared options always win. A `select` served with an empty
 * option list strands the broker on a required field, and the vocabulary is
 * closed and known here — so the honest move is to render it and record the
 * contract gap, not to leave an empty dropdown on screen.
 */
export const MARITAL_STATUS_OPTIONS: BondFieldOption[] = MARITAL_STATUSES.map(
  ({ value, label }) => ({ value, label }),
);

/** How a stored marital status reads, or null when it is one this build has
 *  never heard of (which is the same answer as unanswered: we do not know). */
export function maritalStatusLabel(value: unknown): string | null {
  return MARITAL_STATUSES.find((s) => s.value === value)?.label ?? null;
}

/**
 * Whether this person's spouse is on the signer list — or **null** for "we do
 * not know yet".
 *
 * Three answers, not two, and the third is the load-bearing one: an unanswered
 * (or unrecognised) marital status is not "no spouse", it is an open question,
 * and the rule's own word for it is `needs_information`. Collapsing it to false
 * is how an owner's spouse quietly stops being asked for.
 */
export function spouseSigns(value: unknown): boolean | null {
  const member = MARITAL_STATUSES.find((s) => s.value === value);
  return member ? member.spouseSigns : null;
}

// ── Scoped names (UI only — see the header) ──────────────────────────────────

const SCOPE = "::";

/** `alice-id` + `personal[].firstName` → `alice-id::personal[].firstName`. */
export function scopedRosterName(ownerId: string, path: string): string {
  return `${ownerId}${SCOPE}${path}`;
}

/**
 * The `{ownerId, path}` a scoped name carries, or null for a plain one.
 *
 * SYMMETRIC with `scopedRosterName` (BRK-S155): a name CONTAINING the separator
 * is scoped, including one that starts with it. It used to answer null for
 * `::path` — an owner id of zero length — which made it a "plain" key to every
 * rule written against this function, so it would have passed the roster's
 * filters and gone onto the wire as a contract path nobody declared. An empty
 * owner id matches nobody on any roster, so the name is now DROPPED like any
 * other scoped key for somebody who is not there.
 */
export function unscopeRosterName(
  name: string,
): { ownerId: string; path: string } | null {
  const at = name.indexOf(SCOPE);
  if (at < 0) return null;
  return {
    ownerId: name.slice(0, at),
    path: name.slice(at + SCOPE.length),
  };
}

/**
 * A step's OWN bag as an ORDER publishes it — the contract's entry-list
 * spelling, which is what a resume holds and what `rosterFormValues`
 * distributes.
 *
 * **`null` is not an empty step.** It is an order that said nothing about this
 * one, and the difference decides whether a save may replace what the broker
 * has typed: replacing a bag on the word of a response that never claimed it
 * would discard their work (BRK-S155).
 */
export function rosterStepValues(
  order: { values?: Record<string, Record<string, unknown>> },
  stepKey: string,
): BondValueBag | null {
  const bag = order.values?.[stepKey];
  return bag ? (bag as BondValueBag) : null;
}

/** The CONTRACT path a form field name addresses — itself, for an unscoped
 *  one. What every rule keyed by path (the PII paths, the owner exclusions) is
 *  written against. */
export function contractPathOf(name: string): string {
  return unscopeRosterName(name)?.path ?? name;
}

/**
 * The keys a step-save body may NOT carry — the last line of defence
 * (BRK-S154).
 *
 * Scoped names are UI state, and by the time one is on a body it is a contract
 * path this portal invented: PMS's `isRepeatedPath` does not match it, so it is
 * relayed verbatim, and discovery's deal-body mapper splits it into a BLOCK
 * nobody declared (`Unrecognized keys: "<id>::personal"`). Every value on that
 * save is refused, including the ones that were correct.
 *
 * This is NOT the fix for any particular leak — `rosterWireValues` is where a
 * scoped name is supposed to stop. It is the assertion that it did, made in the
 * one place a body is assembled, so the next way in fails here with a sentence
 * rather than at the far end of two services with a vendor's.
 */
export function invalidWireKeys(values: Record<string, unknown>): string[] {
  return Object.keys(values).filter((key) => key.includes(SCOPE));
}

/** What the broker is told when it does. Non-optimistic on purpose: nothing was
 *  saved, so the sentence must not suggest otherwise, and it names the keys so
 *  a support conversation starts from the evidence. */
export function invalidWireKeyMessage(keys: readonly string[]): string {
  const shown = keys.slice(0, 3).join(", ");
  const rest = keys.length > 3 ? `, and ${keys.length - 3} more` : "";
  return `This step could not be saved — the form produced an invalid field name (${shown}${rest}); reload the order and try again.`;
}

/** A stable identity for somebody this portal is adding, who has no PMS record
 *  yet. Declared on the save, so discovery and PMS adopt it and it comes back
 *  on every later read — the caller-stated identity the contract asks for. */
export function mintOwnerId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  // Prefixed so an id this portal minted is recognisable in a support
  // conversation, and bounded well inside discovery's 128.
  return `brk-${random}`;
}

// ── The roster's people ──────────────────────────────────────────────────────

/**
 * One person on the roster as the form draws them.
 *
 * `spouseOf` is the whole discriminator: null is an OWNER, an `ownerId` is the
 * SPOUSE of that person. It is a contract path (`personal[].spouseOf`), so the
 * roster is recoverable from the saved order alone and the form holds no
 * private idea of who is whose.
 */
export interface RosterPerson {
  ownerId: string;
  /** How they are named on the roster declaration. Derived from what has been
   *  typed, because `displayName` is required upstream and a blank is a 422. */
  displayName: string;
  spouseOf: string | null;
}

/** The house default, mirrored from the design (§ 1) so the copy has something
 *  true to say before any rule has been resolved. */
export const HOUSE_INDEMNITY_RULE: BondIndemnityRule = {
  owners: { minimumShare: 10, representedShare: 50 },
  spouses: "all",
};

/**
 * Lendflow's `personal[]` limit, surfaced as a SOFT limit on the roster
 * (reconciliation decision (c)).
 *
 * Soft because it is a vendor ceiling rather than an underwriting rule: the
 * portal stops offering "+ Add owner" and says why, and nothing already typed is
 * discarded. It counts the WHOLE roster — spouses included — because they are
 * elements of the same block. ⚠️ Whether the cap is on the roster or on the
 * owners alone is a question for PMS; counting the block is the conservative
 * reading, since it is the one that cannot overflow the vendor's array.
 */
export const ROSTER_SOFT_LIMIT = 10;

/** What a person is called before anything has been typed. Exported for the
 *  roster checks that name a person in a sentence (BRK-S317). */
export const UNNAMED = "New owner";

function nameFrom(first: unknown, last: unknown): string {
  const parts = [first, last]
    .filter((p): p is string => typeof p === "string" && p.trim() !== "")
    .map((p) => p.trim());
  return parts.length > 0 ? parts.join(" ") : UNNAMED;
}

/**
 * The roster a saved step describes, read from the ORDER's own two facts: the
 * declared roster (`owners[]`) and the entry lists in its value bag.
 *
 * There is ALWAYS at least one owner: a roster step with nothing typed yet is a
 * form asking for the first person, not an empty list.
 *
 * A path PMS COLLAPSED to a scalar (its broker view does that today) still
 * reads — as the PRIMARY owner's answer, which is exactly what it is. That is
 * what lets an order filed before PMS-S393 resume into the roster with its
 * answers intact rather than blank.
 */
export function rosterRowsFromValues(
  bag: BondValueBag,
  owners: readonly BondOwner[] | undefined,
  /**
   * The identity to give the FIRST person when neither the roster nor the
   * values name anybody.
   *
   * Required to be STABLE, and the reason is a defect this function had:
   * minting one here made the answer different on every render, so the block
   * the broker was typing into had a different identity from the one the page
   * had just stored — and a spouse added against the old id was drawn against
   * nobody. The caller derives it from the ORDER, which is stable for as long
   * as the roster it seeds.
   */
  seedOwnerId?: string,
): RosterPerson[] {
  const seen = new Map<string, RosterPerson>();
  const add = (ownerId: string) => {
    if (!seen.has(ownerId)) {
      seen.set(ownerId, { ownerId, displayName: UNNAMED, spouseOf: null });
    }
    return seen.get(ownerId) as RosterPerson;
  };

  // The DECLARED roster first, in its own order — a person who answered nothing
  // is still on it, still to be drawn (PMS-S244's whole point).
  for (const owner of owners ?? [])
    add(owner.ownerId).displayName = owner.displayName || UNNAMED;

  // Then anybody an entry list names. A value bag naming somebody the roster
  // does not is a roster that fell behind, never a reason to drop their answers.
  for (const value of Object.values(bag)) {
    for (const entry of elementEntries(value) ?? []) add(entry.ownerId);
  }

  if (seen.size === 0) {
    const first = seedOwnerId ?? mintOwnerId();
    seen.set(first, { ownerId: first, displayName: UNNAMED, spouseOf: null });
  }

  for (const person of seen.values()) {
    const first = readFor(bag, FIRST_NAME_PATH, person.ownerId, owners);
    const last = readFor(bag, LAST_NAME_PATH, person.ownerId, owners);
    const named = nameFrom(first, last);
    if (named !== UNNAMED || person.displayName === UNNAMED) {
      person.displayName = named;
    }
    const link = readFor(bag, SPOUSE_OF_PATH, person.ownerId, owners);
    person.spouseOf = typeof link === "string" && link !== "" ? link : null;
  }
  return [...seen.values()];
}

/**
 * One person's answer to one contract path.
 *
 * Entry-shaped values resolve by IDENTITY. A collapsed scalar resolves to the
 * PRIMARY owner and to nobody else — attributing it to whoever is being drawn
 * would be the positional inference by another route.
 */
export function readFor(
  bag: Readonly<Record<string, unknown>>,
  path: string,
  ownerId: string,
  owners: readonly BondOwner[] | undefined,
): unknown {
  const held = bag[path];
  const entries = elementEntries(held);
  if (entries) return entryForOwner(entries, ownerId)?.value;
  if (held === undefined) return undefined;
  const primary = owners?.find((o) => o.isPrimary)?.ownerId;
  return primary !== undefined && primary === ownerId ? held : undefined;
}

export function ownerPeople(roster: readonly RosterPerson[]): RosterPerson[] {
  return roster.filter((person) => person.spouseOf === null);
}

export function spouseOf(
  roster: readonly RosterPerson[],
  ownerId: string,
): RosterPerson | undefined {
  return roster.find((person) => person.spouseOf === ownerId);
}

/**
 * The roster in READING order: each owner, then their spouse.
 *
 * Declaration order is not reading order — a spouse is added when the owner
 * says they are married, so they land after everybody who existed then, which
 * for a second owner's spouse is in the middle of nobody. The RULE's ordering
 * (largest share first) is deliberately NOT applied: the form is the cap table
 * being typed, and re-sorting blocks under the cursor as a percentage is
 * entered is how a broker loses their place.
 */
export function orderedRoster(roster: readonly RosterPerson[]): RosterPerson[] {
  return ownerPeople(roster).flatMap((owner) => {
    const spouse = spouseOf(roster, owner.ownerId);
    return spouse ? [owner, spouse] : [owner];
  });
}

/**
 * Whether this person has answered ANYTHING on this step — read from the bag in
 * both of its spellings, because the page's bag holds both at once: the order's
 * own entry lists (and the primary owner's collapsed scalar) and the broker's
 * SCOPED keystrokes laid over them.
 *
 * A blank is not an answer. A field typed into and then cleared leaves `""`
 * behind, and treating that as a person would keep an empty panel on screen for
 * exactly as long as the bag survives.
 */
export function hasRosterAnswer(
  bag: BondValueBag,
  ownerId: string,
  owners: readonly BondOwner[] | undefined,
): boolean {
  const answered = (value: unknown) =>
    value !== undefined && value !== null && value !== "";
  for (const [key, value] of Object.entries(bag)) {
    const scoped = unscopeRosterName(key);
    if (scoped) {
      if (scoped.ownerId === ownerId && answered(value)) return true;
      continue;
    }
    if (!isRepeatedPath(key)) continue;
    if (answered(readFor(bag, key, ownerId, owners))) return true;
  }
  return false;
}

/**
 * The roster the form actually DRAWS — a subset of the one the order declares.
 *
 * ⚠️ A DECLARED OWNER WHO HAS ANSWERED NOTHING IS A CHECKLIST SEAT, NOT AN
 * EMPTY PANEL (BRK-S156). `owners[]` is re-derived from the insured's account
 * on every save, so a pushing save answers with every person on that record —
 * including people no field on this step has ever collected. Drawing one block
 * per declared owner meant a broker hit Continue, the save succeeded, and the
 * step came back with a fresh empty owner panel open under their cap table: it
 * reads as a form that wants more, when what it wants is nothing at all.
 *
 * The seat is still SAID — the checklist names them `missing` and offers the
 * jump, and `undrawnOwners` is what "+ Add owner" adopts so re-opening the
 * block keeps their identity rather than minting a duplicate. That is the
 * difference this rule turns on: a person is announced, and drawn when the
 * broker asks for them.
 *
 * NEVER EMPTY. A step with nobody on it is a form asking for the first person,
 * so the first owner survives however little has been typed — which is also the
 * untouched-roster case, where the seeded person has answered nothing by
 * definition.
 *
 * Spouse rows are unaffected: a spouse row exists only because
 * `personal[].spouseOf` names one, which is itself an answer.
 */
export function drawnRoster(
  roster: readonly RosterPerson[],
  bag: BondValueBag,
  owners: readonly BondOwner[] | undefined,
): RosterPerson[] {
  const kept = roster.filter(
    (person) =>
      person.spouseOf !== null || hasRosterAnswer(bag, person.ownerId, owners),
  );
  if (ownerPeople(kept).length > 0) {
    // A spouse whose owner is no longer drawn has nobody to sit under, so they
    // go with them — the same rule `removeOwner` applies.
    return kept.filter(
      (person) =>
        person.spouseOf === null ||
        kept.some((other) => other.ownerId === person.spouseOf),
    );
  }
  const first = ownerPeople(roster)[0];
  return first ? [first] : [];
}

/** The declared owners the form is NOT drawing — what "+ Add owner" adopts
 *  before it mints anybody, so re-opening a block keeps the identity PMS
 *  already holds instead of creating a second personnel row for one person. */
export function undrawnOwners(
  roster: readonly RosterPerson[],
  drawn: readonly RosterPerson[],
): RosterPerson[] {
  return ownerPeople(roster).filter(
    (person) => !drawn.some((other) => other.ownerId === person.ownerId),
  );
}

/** "+ Add owner" — one more person on the CAP TABLE. Never a choice about who
 *  signs: somebody added below the line is kept and simply not listed.
 *
 *  `adopt` re-opens a person the order already declares (see `undrawnOwners`);
 *  with nobody to adopt, a fresh identity is minted. */
export function addOwner(
  roster: readonly RosterPerson[],
  adopt?: RosterPerson,
): RosterPerson[] {
  return [
    ...roster,
    adopt
      ? { ...adopt, spouseOf: null }
      : { ownerId: mintOwnerId(), displayName: UNNAMED, spouseOf: null },
  ];
}

/** The owner's spouse, created if they have none. Idempotent — flipping
 *  between two spouse-signing statuses must not strand a second person. */
export function withSpouse(
  roster: readonly RosterPerson[],
  ownerId: string,
): RosterPerson[] {
  if (spouseOf(roster, ownerId)) return [...roster];
  return [
    ...roster,
    { ownerId: mintOwnerId(), displayName: UNNAMED, spouseOf: ownerId },
  ];
}

export function withoutSpouse(
  roster: readonly RosterPerson[],
  ownerId: string,
): RosterPerson[] {
  return roster.filter((person) => person.spouseOf !== ownerId);
}

/** Removing an owner removes their spouse too: a spouse seat exists only
 *  because an owner is listed, so leaving them behind would file somebody
 *  married to nobody on the order. Never the last owner. */
export function removeOwner(
  roster: readonly RosterPerson[],
  ownerId: string,
): RosterPerson[] {
  if (ownerPeople(roster).length <= 1) return [...roster];
  return roster.filter(
    (person) => person.ownerId !== ownerId && person.spouseOf !== ownerId,
  );
}

/** Whether the vendor's ceiling leaves room for another owner — counted over
 *  the WHOLE block, spouses included. */
export function rosterHasRoom(roster: readonly RosterPerson[]): boolean {
  return roster.length < ROSTER_SOFT_LIMIT;
}

/**
 * The roster with every `displayName` refreshed from what is currently TYPED.
 *
 * `displayName` is required upstream and a blank is a 422, so it cannot be left
 * at whatever the last read said: the broker has usually just typed the name of
 * somebody the server has never heard of. Read from the FORM bag, because that
 * is where the keystrokes are.
 */
export function namedRoster(
  roster: readonly RosterPerson[],
  form: BondValueBag,
): RosterPerson[] {
  return roster.map((person) => ({
    ...person,
    displayName: nameFrom(
      form[scopedRosterName(person.ownerId, FIRST_NAME_PATH)],
      form[scopedRosterName(person.ownerId, LAST_NAME_PATH)],
    ),
  }));
}

// ── The PRIMARY indemnitor (BRK-S156) ────────────────────────────────────────
//
// The one owner the personal credit check is run against. It used to be the
// first block by position — which is an inference, and the one the owner roster
// exists to retire: the broker types their cap table in whatever order they
// have it to hand, and the person whose bureau file the underwriting turns on
// is rarely the one they happened to key first.
//
// So it is a CHOICE with a DEFAULT, and the default is a fact about the cap
// table rather than about the form: the largest share, ties broken on roster
// order. The broker may move it, the save declares it (`isPrimary`), and the
// answer that comes back is the one that is rendered — see
// `effectivePrimaryOwnerId`.

export const PRIMARY_INDEMNITOR_LABEL = "Primary indemnitor";
export const PRIMARY_INDEMNITOR_NOTE =
  "The personal credit check is run against this person.";

/**
 * One owner's declared share, or 0 for somebody who has not said. Zero rather
 * than null so the comparison has nothing to special-case: an owner with no
 * share never outranks one with a share.
 *
 * Read in BOTH of the bag's spellings for the reason `hasRosterAnswer` is — the
 * scoped keystroke first, then the order's own entry list — so the default
 * holds on a RESUMED order, where nothing has been typed and every share is an
 * entry. Reading only the scoped half made every resumed roster default to its
 * first block, which is the positional answer this control exists to replace.
 */
function shareOf(
  form: BondValueBag,
  ownerId: string,
  owners: readonly BondOwner[] | undefined,
): number {
  const held =
    form[scopedRosterName(ownerId, OWNERSHIP_PATH)] ??
    readFor(form, OWNERSHIP_PATH, ownerId, owners);
  const share =
    typeof held === "number"
      ? held
      : typeof held === "string" && held.trim() !== ""
        ? Number(held)
        : Number.NaN;
  return Number.isFinite(share) ? share : 0;
}

/** The largest share on the roster, ties broken on ROSTER ORDER — which is the
 *  same tie-break the signer procedure uses, and for the same reason: an order
 *  the broker can see beats a comparison they cannot. */
export function defaultPrimaryOwnerId(
  roster: readonly RosterPerson[],
  form: BondValueBag,
  owners?: readonly BondOwner[],
): string | undefined {
  let best: RosterPerson | undefined;
  let bestShare = Number.NEGATIVE_INFINITY;
  for (const owner of ownerPeople(roster)) {
    const share = shareOf(form, owner.ownerId, owners);
    if (share > bestShare) {
      best = owner;
      bestShare = share;
    }
  }
  return best?.ownerId;
}

/**
 * WHO the form marks primary, in priority order: the broker's own pick since
 * the last read, then the roster the ORDER declares, then the default above.
 *
 * ⚠️ THE RESPONSE WINS, AND THERE IS NO CLIENT-SIDE OVERRIDE. A save declares
 * the broker's choice; what comes back is `owners[]`, and until PMS-S403 lands
 * PMS may honour a different one (it echoes the largest share). Rendering the
 * broker's pick over the top of that would show them a primary the service has
 * not accepted — a promise about whose credit is pulled, made by the client. So
 * the page DROPS the pick when the save answers, exactly as it drops the
 * layout, and this function then reads the declaration.
 */
export function effectivePrimaryOwnerId(
  roster: readonly RosterPerson[],
  form: BondValueBag,
  owners: readonly BondOwner[] | undefined,
  chosen: string | undefined,
): string | undefined {
  const drawn = (id: string | undefined) =>
    id !== undefined && ownerPeople(roster).some((p) => p.ownerId === id);
  if (drawn(chosen)) return chosen;
  const declared = owners?.find((owner) => owner.isPrimary)?.ownerId;
  if (drawn(declared)) return declared;
  return defaultPrimaryOwnerId(roster, form, owners);
}

/** The roster DECLARATION a save carries. Exactly one primary, which is the
 *  rule upstream enforces with a 422 — the owner the broker marked, falling
 *  back to the first in reading order when nobody is marked (an unreachable
 *  state on screen, and a declaration with no primary is not one to send). */
export function ownerDeclarations(
  roster: readonly RosterPerson[],
  primaryOwnerId?: string,
): BondOwnerDeclaration[] {
  const owners = ownerPeople(roster);
  const primary =
    owners.find((person) => person.ownerId === primaryOwnerId)?.ownerId ??
    owners[0]?.ownerId;
  return orderedRoster(roster).map((person) => ({
    ownerId: person.ownerId,
    displayName: person.displayName || UNNAMED,
    isPrimary: person.ownerId === primary,
  }));
}

/**
 * What a COLLAPSED card puts in its header (BRK-S164): the person, their
 * share, and the spouse sitting inside the card the broker cannot currently
 * see.
 *
 * Read from the FORM bag rather than from `RosterPerson.displayName`, for the
 * reason `namedRoster` exists: the stored name is whatever the last read said,
 * and a broker who has just typed one is looking at a header that would
 * otherwise still say "New owner".
 *
 * The spouse's line is what makes the collapse safe to offer at all. A spouse
 * is drawn inside their owner's card, so collapsing it hides a second person
 * entirely — and a header that did not mention them would read as an owner who
 * never said they were married.
 */
export interface RosterCardSummary {
  ownerId: string;
  name: string;
  /** Their declared share, formatted for display — null when they have not
   *  said. Never a formatted string on the wire (D-10). */
  share: string | null;
  /** Their spouse, when one is linked — named, or said to be unnamed. `null`
   *  is "no spouse on this card", which is a different fact. */
  spouseName: string | null;
}

/** What a spouse row is called before anybody has typed their name. Not
 *  `UNNAMED` — "New owner" on a spouse's line would put a third person on the
 *  card. */
const UNNAMED_SPOUSE = "not yet named";

export function rosterCardSummaries(
  roster: readonly RosterPerson[],
  form: BondValueBag,
): RosterCardSummary[] {
  const named = namedRoster(roster, form);
  return ownerPeople(named).map((owner) => {
    const spouse = spouseOf(named, owner.ownerId);
    const held = form[scopedRosterName(owner.ownerId, OWNERSHIP_PATH)];
    const share =
      typeof held === "number"
        ? held
        : typeof held === "string" && held.trim() !== ""
          ? Number(held)
          : Number.NaN;
    return {
      ownerId: owner.ownerId,
      name: owner.displayName,
      share: Number.isFinite(share) ? `${formatShare(share)}%` : null,
      spouseName: spouse
        ? spouse.displayName === UNNAMED
          ? UNNAMED_SPOUSE
          : spouse.displayName
        : null,
    };
  });
}

// ── The blocks the step draws ────────────────────────────────────────────────

export interface RosterBlock {
  kind: "owner" | "spouse";
  /** The roster identity this block writes for. */
  ownerId: string;
  /** The person this block is ABOUT — itself, for an owner block. */
  aboutOwnerId: string;
  /** 1-based, for the heading. Owner blocks only; a spouse names its owner. */
  position: number;
  fields: BondRenderField[];
}

export interface RosterBlockOptions {
  /** Spouses the broker says have an address of their own. A spouse not in
   *  here shares their owner's, copied at save. */
  spouseAddressDiffers?: ReadonlySet<string>;
}

/**
 * The configuration's repeated fields, drawn once per person, SCOPED to them.
 *
 * `spouseOf` is never drawn: it is structural, written by the form at save
 * (`rosterWireValues`), and a control for "which owner is this the spouse of"
 * would be the broker choosing a relationship rather than stating one.
 *
 * A field the configuration declares that does NOT repeat stays out of the
 * roster entirely — it belongs to the order, not to a person, and is returned
 * by `rosterOrderFields`.
 *
 * ⚠️ REQUIREDNESS IS RESOLVED HERE, ONCE (BRK-S165). The same declared field is
 * drawn for an owner and for a spouse, and they do not owe the same answers, so
 * `requiredForRole` is applied as the field is scoped — the controls and every
 * validator downstream read the SAME `required`, because they read the same
 * field.
 */
export function rosterBlocks(
  fields: readonly BondRenderField[],
  roster: readonly RosterPerson[],
  options: RosterBlockOptions = {},
): RosterBlock[] {
  const differs = options.spouseAddressDiffers ?? new Set<string>();
  const repeated = fields.filter((field) => isRepeatedPath(field.name));
  let position = 0;
  return orderedRoster(roster).map((person) => {
    const spouse = person.spouseOf !== null;
    if (!spouse) position += 1;
    const forPerson = repeated.filter((field) => {
      if (field.name === SPOUSE_OF_PATH) return false;
      if (!spouse) return true;
      if (OWNER_ONLY_PATHS.includes(field.name)) return false;
      // The spouse's address is asked for ONLY when the broker says it differs.
      // Unticked, the owner's is copied at save — the same answer with one
      // fewer thing to type, and the reason the field is absent rather than
      // pre-filled: a pre-filled copy drifts the moment the owner's changes.
      if (fieldKind(field) === "address") return differs.has(person.ownerId);
      return true;
    });
    const role: RosterRole = spouse ? "spouse" : "owner";
    return {
      kind: role,
      ownerId: person.ownerId,
      aboutOwnerId: spouse ? (person.spouseOf as string) : person.ownerId,
      position,
      fields: forPerson.map((field) => ({
        ...field,
        name: scopedRosterName(person.ownerId, field.name),
        required: requiredForRole(field, role),
      })),
    };
  });
}

/** The step's fields that do NOT repeat — order-level answers on a roster step,
 *  drawn once above the blocks. */
export function rosterOrderFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter((field) => !isRepeatedPath(field.name));
}

/** Every rendered field on a roster step, flat — what the required checklist,
 *  the format rules and the save all read, so the gate and the controls cannot
 *  disagree about what is owed (the `fieldsForStep` rule). */
export function rosterFields(
  blocks: readonly RosterBlock[],
): BondRenderField[] {
  return blocks.flatMap((block) => block.fields);
}

/**
 * The marital-status field with its fallback options attached.
 *
 * Applied to the TEMPLATE field before the blocks are built, so the select the
 * broker sees, the required checklist and the save all read one field.
 */
export function withMaritalStatusOptions(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.map((field) => {
    if (field.name !== MARITAL_STATUS_PATH) return field;
    if (field.options && field.options.length > 0) return field;
    return { ...field, type: "select", options: MARITAL_STATUS_OPTIONS };
  });
}

/**
 * The form's SCOPED bag, seeded from an order's own values.
 *
 * Entry lists are distributed by identity; a collapsed scalar reaches the
 * primary owner and nobody else (`readFor`). Non-repeated paths pass straight
 * through under their own name.
 *
 * ⚠️ IT SCOPES NOTHING TWICE, whichever field list it is handed (BRK-S154).
 * Two lists describe a roster step — the configuration's TEMPLATE
 * (`personal[].firstName`) and what the form DRAWS (one scoped name per person)
 * — and the save handed over the second. Every name was then scoped again, and
 * `readFor`'s collapsed-scalar fallback is what gave it something to scope:
 * once the order declares a primary owner, the primary reads any bare key as
 * their own, and every OTHER person's scoped key in the bag is a bare key to
 * it. The result was `<primary>::<someone>::personal[].firstName`, which
 * `unscopeRosterName` peels ONCE — so the save's "contract path" was still
 * scoped, and discovery answered with a block nobody declared. Working in
 * contract paths here is what makes the function idempotent, rather than
 * correct only for the callers that remember which list to pass.
 */
export function rosterFormValues(
  bag: BondValueBag,
  roster: readonly RosterPerson[],
  fields: readonly BondRenderField[],
  owners: readonly BondOwner[] | undefined,
): BondValueBag {
  const out: BondValueBag = {};
  for (const [key, value] of Object.entries(bag)) {
    // A scoped key is never an order-level one, whatever its path looks like —
    // it is somebody's answer, and it belongs to the overlay, not here.
    if (!isRepeatedPath(key) && unscopeRosterName(key) === null)
      out[key] = value;
  }
  for (const person of roster) {
    for (const field of fields) {
      const path = contractPathOf(field.name);
      if (!isRepeatedPath(path)) continue;
      const held = readFor(bag, path, person.ownerId, owners);
      if (held === undefined) continue;
      out[scopedRosterName(person.ownerId, path)] =
        held as BondValueBag[string];
    }
  }
  return out;
}

// ── What goes on the wire ────────────────────────────────────────────────────

/**
 * The scoped form bag, turned back into the CONTRACT's shape: one
 * `{ownerId, value}` entry list per repeated path, plus the two facts the form
 * knows and the broker never typed — every spouse linked to their owner, and
 * every shared address copied across.
 *
 * ⚠️ NO SCOPED NAME SURVIVES THIS, and no `spouseOf: null` is ever written. On
 * the discovery side an explicit `null` means **clear this person's owner
 * link** (contract 8.1.0), so emitting one for somebody the form no longer
 * draws would unlink a person rather than remove them. Removing an owner
 * removes the spouse from the ROSTER (`removeOwner`), and only people still on
 * it are linked here — so the pointer is written or it is absent, never nulled.
 *
 * A person with no answer to a path simply has NO ENTRY: the contract's rule,
 * and the reason there is no positional hole to fill.
 */
export function rosterWireValues(
  bag: BondValueBag,
  roster: readonly RosterPerson[],
  fields: readonly BondRenderField[],
  options: RosterBlockOptions = {},
): Record<string, unknown> {
  const differs = options.spouseAddressDiffers ?? new Set<string>();
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(bag)) {
    if (unscopeRosterName(key) === null && value !== undefined)
      out[key] = value;
  }

  const addressPaths = fields
    .filter((field) => fieldKind(field) === "address")
    .map((field) => contractPathOf(field.name));
  const entries = new Map<string, BondElementValue[]>();
  const push = (path: string, ownerId: string, value: unknown) => {
    if (value === undefined) return;
    const list = entries.get(path) ?? [];
    list.push({ ownerId, value });
    entries.set(path, list);
  };

  for (const person of orderedRoster(roster)) {
    const read = (path: string) => bag[scopedRosterName(person.ownerId, path)];
    for (const [key, value] of Object.entries(bag)) {
      const scoped = unscopeRosterName(key);
      if (!scoped || scoped.ownerId !== person.ownerId) continue;
      push(scoped.path, person.ownerId, value);
    }
    if (person.spouseOf === null) continue;
    push(SPOUSE_OF_PATH, person.ownerId, person.spouseOf);
    if (differs.has(person.ownerId)) continue;
    for (const path of addressPaths) {
      if (read(path) !== undefined) continue;
      const owners = bag[scopedRosterName(person.spouseOf, path)];
      if (owners !== undefined) push(path, person.ownerId, owners);
    }
  }
  for (const [path, list] of entries) out[path] = list;
  return out;
}

/**
 * Whether a spouse's stored address differs from their owner's — the seed for
 * the "address differs" checkbox on a RESUMED order.
 *
 * Derived rather than stored, deliberately: the checkbox is a question about
 * two values the order already carries, and a BRK-owned key answering it would
 * put a field on the wire that no configuration declared and PMS would have to
 * relay forever.
 */
export function spouseAddressDiffers(
  bag: BondValueBag,
  fields: readonly BondRenderField[],
  person: RosterPerson,
): boolean {
  if (person.spouseOf === null) return false;
  return fields
    .filter((field) => fieldKind(field) === "address")
    .some((field) => {
      const path = contractPathOf(field.name);
      const mine = bag[scopedRosterName(person.ownerId, path)];
      if (mine === undefined) return false;
      const theirs = bag[scopedRosterName(person.spouseOf as string, path)];
      return JSON.stringify(mine) !== JSON.stringify(theirs);
    });
}

// ── Copy ─────────────────────────────────────────────────────────────────────

/**
 * What the roster tells the broker about who must be included — THE ONE LINE
 * under the progress bar (BRK-S164).
 *
 * ⚠️ RECONCILIATION DECISION (a). Greg's screen reads "all owners with greater
 * than 10% ownership must be included". The agreed rule and the configuration
 * are AT LEAST the minimum share — inclusive, Product's own word — so a 10%
 * owner is included and the screen's wording excludes exactly the person the
 * threshold was written to catch. The copy follows the CONFIGURED rule, and the
 * number is read from it rather than written into the sentence, so a product on
 * 25% says 25.
 *
 * The spouse clause is DROPPED on a rule that asks for no spouses
 * (`spouses: "none"`), rather than reworded: a sentence that mentions spouses
 * on a product with no spouse seats is a requirement the broker will try to
 * meet, and there is no block on screen for them to meet it in.
 */
export function ownersIncludedCopy(rule: BondIndemnityRule): string {
  const owners = `All owners with at least ${formatShare(rule.owners.minimumShare)}% must be listed`;
  return spousesRequired(rule)
    ? `${owners}, plus spouses of married owners.`
    : `${owners}.`;
}

/**
 * The top-up half of the rule as a PROGRESS reading — how much ownership is
 * listed, against the line it has to pass.
 *
 * `null` when the rule has no line (`representedShare: null`), and that absence
 * is the whole reason this returns an object rather than a number: a bar
 * filling toward a line that does not exist would invent a target, and the
 * minimum-share rule alone is the honest rendering of that product.
 *
 * `percent` fills TOWARD the line rather than toward 100% ownership: the line
 * is what the broker has to clear, so a 30-of-50 roster is a bar at 60% and not
 * at 30%. It is clamped, so passing the line reads full rather than overflowing
 * — `met` is the fact, and it is EXCLUSIVE (`>`), Product's own word.
 */
export interface IndemnityProgress {
  /** Combined share of the listed owners, as the evaluation counted it. */
  listed: number;
  /** The rule's line — `representedShare`. */
  line: number;
  /** How full the bar is, 0–100, clamped. */
  percent: number;
  met: boolean;
  label: string;
}

export function indemnityProgress(
  indemnity: BondIndemnity | null | undefined,
): IndemnityProgress | null {
  const line = indemnity?.rule.owners.representedShare;
  if (!indemnity || line === null || line === undefined) return null;
  const raw = indemnity.representedShareListed;
  const listed = Number.isFinite(raw) ? raw : 0;
  const met = listed > line;
  const percent =
    line <= 0
      ? 100
      : Math.max(0, Math.min(100, Math.round((listed / line) * 100)));
  return {
    listed,
    line,
    percent,
    met,
    label: `${formatShare(listed)}% of ownership listed · more than ${formatShare(line)}% required`,
  };
}

/** What the progress bar is called, on screen and to a screen reader. */
export const INDEMNITY_PROGRESS_LABEL = "Ownership represented";

/** Whether spouses are on this rule at all — the roster draws no spouse block
 *  when they are not, whatever a marital status says. */
export function spousesRequired(rule: BondIndemnityRule): boolean {
  return rule.spouses === "all";
}

/** The rule this screen should quote: the one the EVALUATION used, falling back
 *  to the configuration's, falling back to the house default. */
export function effectiveIndemnityRule(
  indemnity: BondIndemnity | null | undefined,
  configured: BondIndemnityRule | undefined,
): BondIndemnityRule {
  return indemnity?.rule ?? configured ?? HOUSE_INDEMNITY_RULE;
}

/** A percentage as the broker reads it — no trailing `.0`, and never a
 *  formatted string on the wire (D-10). */
export function formatShare(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return String(Math.round(value * 100) / 100);
}

// ── The seats, said ON the card they are about (BRK-S164) ───────────────────
//
// ⚠️ THE YELLOW CHECKLIST BOX IS GONE, AND THIS IS WHAT REPLACES IT.
//
// `BondSignerChecklist` listed every seat in a card of its own, above a stack
// of owner blocks, and offered a "Go to this owner" jump between the two. What
// Aaron's 2026-09-21 walk showed is that the jump is the tell: a note about a
// person, rendered somewhere other than that person, needs navigation to be
// useful at all — and the broker still has to hold "Alice needs a marital
// status" in their head for the length of the scroll. The note now sits ON
// Alice's card, so there is nothing to carry and nothing to jump to.
//
// Nothing about WHO SIGNS moved. The evaluation is still discovery's, this
// module still decides nobody, and these functions read `indemnity.signers`
// and nothing the page worked out — the same rule the checklist was written
// under (BRK-S122: no optimistic success).

/**
 * One seat's note, in the broker's terms — null for a seat that is settled.
 *
 * It is written for a card that IS the person, so it names nobody: "Spouse
 * required" under Alice's own header needs no "of Alice", and the checklist's
 * `${name}’s spouse` phrasing would read as a second person on her card.
 *
 * `needs_information` has two shapes with different fixes, and only one of them
 * is derivable from the seat: a spouse seat is short a MARITAL STATUS (the
 * owner's own control, on this very card), where an owner seat is short
 * something the evaluation does not itemise. So the derivable one says what to
 * do and the other says honestly that it does not know — never a guessed field
 * name, which would send the broker to a control that is already answered.
 */
export function signerNote(signer: BondSigner): string | null {
  if (signer.status === "listed") return null;
  if (signer.status === "missing") {
    return signer.basis === "spouse_of"
      ? "Spouse required — add below"
      : "Not listed yet — complete this owner";
  }
  return signer.basis === "spouse_of"
    ? "Needs a marital status"
    : "Needs more information";
}

/**
 * What ONE owner's card has to say about itself — every seat the evaluation
 * raised against that person, sorted onto the two places the card can put one.
 *
 * Seats are addressed by `subject.ownerId`, which is the OWNER on both bases: a
 * spouse seat's subject is the person they are married to, and `spouseOwnerId`
 * is the spouse's own roster identity (`null` until they are on the roster,
 * which is exactly what `missing` means). So both of an owner's seats land on
 * one card, which is the whole point — a spouse is not a card, they are a
 * fieldset inside their owner's.
 *
 * `attention` is what a COLLAPSED card marks: any unsettled seat, the owner's
 * or their spouse's. It is deliberately one flag rather than a status — a
 * collapsed card has room for a marker and not for a sentence, and the sentence
 * is one click away under Edit.
 */
export interface RosterCardStatus {
  /** Notes about the OWNER themselves. */
  ownerNotes: string[];
  /** Notes about their SPOUSE — rendered on the spouse fieldset when one is
   *  drawn, and under the owner's own header when it is not (which is the
   *  `missing` case: there is no fieldset yet to carry it). */
  spouseNotes: string[];
  /** Whether anything on this card is unsettled. */
  attention: boolean;
}

export function rosterCardStatus(
  indemnity: BondIndemnity | null | undefined,
  ownerId: string,
): RosterCardStatus {
  const status: RosterCardStatus = {
    ownerNotes: [],
    spouseNotes: [],
    attention: false,
  };
  // A block this build cannot READ says nothing rather than guessing: the
  // guarded notice owns that case, exactly as it did for the checklist.
  if (!indemnity || !isIndemnitySupported(indemnity)) return status;
  for (const signer of indemnity.signers) {
    if (signer.subject.ownerId !== ownerId) continue;
    const note = signerNote(signer);
    if (note === null) continue;
    status.attention = true;
    if (signer.basis === "spouse_of") status.spouseNotes.push(note);
    else status.ownerNotes.push(note);
  }
  return status;
}

/** The blocks that make up ONE owner's CARD — their own, plus the spouse
 *  fieldset drawn inside it. `aboutOwnerId` is the owner on both kinds, which
 *  is what makes this one filter rather than two. */
export function cardBlocks(
  blocks: readonly RosterBlock[],
  ownerId: string,
): RosterBlock[] {
  return blocks.filter((block) => block.aboutOwnerId === ownerId);
}

/**
 * The seats that have NO CARD to sit on — and the one thing the checklist did
 * that nothing else does (BRK-S164).
 *
 * `owners[]` is re-derived from the insured's account on every save, so the
 * evaluation can name somebody the form is not drawing: BRK-S156's own rule is
 * that a declared owner who has answered nothing is a seat rather than an
 * empty panel. Under the checklist that seat was simply a row in the list.
 * With the notes on the cards, a seat with no card would be DROPPED — and a
 * `missing` owner silently dropped is the broker being told nothing while the
 * request is short.
 *
 * So these are said under the bar, where the other roster-wide fact lives, and
 * they NAME the person — there is no card around them to say who they are
 * about, which is the whole difference from `signerNote`.
 */
export function unplacedSignerNotes(
  indemnity: BondIndemnity | null | undefined,
  drawnOwnerIds: ReadonlySet<string>,
): string[] {
  if (!indemnity || !isIndemnitySupported(indemnity)) return [];
  const out: string[] = [];
  for (const signer of indemnity.signers) {
    if (drawnOwnerIds.has(signer.subject.ownerId)) continue;
    if (signer.status === "listed") continue;
    const who = signer.subject.name?.trim();
    const subject =
      signer.basis === "spouse_of"
        ? who
          ? `${who}’s spouse`
          : "An owner’s spouse"
        : who || "An owner";
    out.push(
      signer.status === "missing"
        ? `${subject} still has to be listed on this request.`
        : `${subject} needs more information.`,
    );
  }
  return out;
}

/** WHICH CARD a roster identity is drawn on — themselves for an owner, their
 *  owner for a spouse. What a blocked save scrolls to: sending the broker to
 *  "the spouse" is sending them to a fieldset inside a card, and the card is
 *  the thing that can be opened. */
/**
 * Where on the roster a person is, in the words the card headings use
 * (BRK-S319): "primary owner", "owner 2", "spouse of the primary owner". It is
 * what an error summary appends to a field's label, so a missing date of birth
 * on the owner's card can never read as the spouse's beside it.
 *
 * Numbered the way `rosterBlocks` numbers the owner headings; null for an id
 * the roster does not draw.
 */
export function rosterPlaceOf(
  roster: readonly RosterPerson[],
  ownerId: string,
  primaryOwnerId?: string,
): string | null {
  const person = roster.find((p) => p.ownerId === ownerId);
  if (!person) return null;
  const owners = ownerPeople(roster);
  const ownerPlace = (id: string): string => {
    if (id === primaryOwnerId) return "primary owner";
    return `owner ${owners.findIndex((o) => o.ownerId === id) + 1}`;
  };
  if (person.spouseOf === null) return ownerPlace(person.ownerId);
  const of = ownerPlace(person.spouseOf);
  return `spouse of ${of === "primary owner" ? "the primary owner" : of}`;
}

export function cardOwnerIdOf(
  roster: readonly RosterPerson[],
  ownerId: string,
): string {
  return roster.find((p) => p.ownerId === ownerId)?.spouseOf ?? ownerId;
}

/**
 * The step fields that belong to the named owners' CARDS, plus every
 * order-level field.
 *
 * What a PER-OWNER Save validates, and what Save & Continue validates over the
 * open cards (BRK-S164). A spouse's scoped fields answer to their OWNER's card,
 * because that is where they are drawn — validating an owner's card without
 * their spouse's would let a half-filled spouse through a button that said it
 * had checked.
 *
 * Order-level fields belong to no card and are always owed, so they are always
 * in: a step that asks one question of the ORDER must not have it skipped by
 * every per-card save on the page.
 */
export function fieldsForCards(
  fields: readonly BondRenderField[],
  roster: readonly RosterPerson[],
  ownerIds: ReadonlySet<string>,
): BondRenderField[] {
  return fields.filter((field) => {
    const scoped = unscopeRosterName(field.name);
    if (!scoped) return true;
    const person = roster.find((p) => p.ownerId === scoped.ownerId);
    if (!person) return false;
    return ownerIds.has(person.spouseOf ?? person.ownerId);
  });
}

/**
 * Where a blocked save's "Please complete" list renders (BRK-S181): each
 * person's unanswered fields on THEIR card — a spouse's on their owner's, where
 * they are drawn — and only the fields that belong to no one card at step
 * level.
 *
 * The step-level panel sat above the roster, off the top of the viewport, so a
 * broker who pressed a card's Save saw nothing happen. A refusal about one
 * person is said where that person is.
 */
export function missingByCard(
  missing: readonly BondRenderField[],
  roster: readonly RosterPerson[],
): {
  order: BondRenderField[];
  byCard: Record<string, BondRenderField[]>;
} {
  const order: BondRenderField[] = [];
  const byCard: Record<string, BondRenderField[]> = {};
  for (const field of missing) {
    const scoped = unscopeRosterName(field.name);
    if (!scoped) {
      order.push(field);
      continue;
    }
    const card = cardOwnerIdOf(roster, scoped.ownerId);
    (byCard[card] ??= []).push(field);
  }
  return { order, byCard };
}

/**
 * What the whole roster still needs, when the block says `needs_information`
 * and no individual seat explains it.
 *
 * The cap-table case: the declared shares never reach the represented line, so
 * the rule has nobody left to add. The fix is a number, not a person.
 */
export function rosterExplanation(
  indemnity: BondIndemnity | null | undefined,
): string | null {
  if (indemnity?.status !== "needs_information") return null;
  if (indemnity.signers.some((s) => s.status === "needs_information")) {
    return null;
  }
  const line = indemnity.rule.owners.representedShare;
  // Nobody is listed at all — including the untouched roster, where the rule has
  // been run against a cap table with no shares on it yet. "Add the REMAINING
  // owners" would describe a top-up that has not started, so it names the first
  // thing to type instead. A rule with no line has no top-up pass either, so a
  // cap table cannot fall short of one, and this is the only shortfall left.
  if (indemnity.signers.length === 0 || line === null || line === undefined) {
    return "No owner here meets the rule yet — add each owner and the share they hold below.";
  }
  return `The listed owners’ shares cannot reach more than ${formatShare(line)}% — add the remaining owners below.`;
}

// ── The gate (BRK-S156, restated by BRK-S164) ────────────────────────────────
//
// ⚠️ THIS STEP BLOCKS, AND BRK-S152's ONE-TIME PAUSE IS GONE.
//
// The pause showed the refreshed list once and then let the broker walk past a
// roster the underwriter was going to send back. What Aaron saw on sandbox1 is
// what that costs: a save, a return to the same screen, and no way to tell
// whether the screen was asking for more or simply repeating itself.
//
// So the wizard does not advance until the SERVER says the list is complete.
// This is not a second answer to the signer question (`bondRoster.ts`'s first
// rule is intact — nothing here evaluates anybody): it reads `indemnity.status`
// and nothing else. The server-side referral on submit stays exactly where it
// was, as the backstop for an order that reaches it short by some other route.
//
// ⚠️ WHAT BRK-S164 CHANGED IS WHERE THE GATE IS ASKED, NOT WHAT IT ANSWERS.
// S156 read it BEFORE the click, to disable a Continue that sat beside a
// "Save and check" — two buttons for what the broker experiences as one
// intention, with the disabled one carrying the explanation. The rule is now
// read AFTER the save, which is the only moment it can be true of anything the
// broker just typed: the evaluation is upstream, so a pre-click reading is
// always an answer about the PREVIOUS roster. One button, and the server's
// answer decides whether it advances.
//
// It gates on a block it can READ. A product that asks for no personal
// indemnity (`null`), a PMS that predates the block (absent), and a vocabulary
// this build cannot render (the guarded card, which owns its own way out) all
// pass: blocking on those would strand a broker on a step over a rule nobody
// stated.

/** Whether the wizard may walk past a roster step. */
export function isRosterComplete(
  indemnity: BondIndemnity | null | undefined,
  primaryOwnerId: string | undefined,
): boolean {
  if (!indemnity || !isIndemnitySupported(indemnity)) return true;
  if (!isIndemnityComplete(indemnity)) return false;
  // Always true by the time a complete list exists — every drawn roster has a
  // primary, because the default is a fact about the cap table. Asserted rather
  // than assumed, because the declaration upstream is a 422 without one.
  return primaryOwnerId !== undefined;
}

/**
 * The ONE primary action on a roster step (BRK-S164).
 *
 * It names both halves because the step does both and the order matters: the
 * save is what refreshes the evaluation, and the advance is what the answer
 * decides. A bare "Continue" would promise a move this button cannot always
 * make; "Save and check" promised the opposite — that it would never move —
 * and then sat next to a dead Continue saying so again.
 */
export const ROSTER_CONTINUE_LABEL = "Save & Continue";

/**
 * What a completed save that did NOT settle the list says — the fallback, and
 * deliberately short.
 *
 * The evidence is on the cards now (`rosterCardStatus`), so this line's whole
 * job is to say that the save landed and the step is not finished; repeating
 * the notes here would be the checklist again, one card lower down.
 * `rosterExplanation` is preferred over it wherever the shortfall belongs to
 * the ROSTER rather than to a person, because no card can carry that one.
 */
export const ROSTER_INCOMPLETE_NOTICE =
  "Not quite — see the notes on the cards above.";
